<i18n>
{
  "fr": {
    "configurator_category_dual_select": {
      "category": {
        "label": "Catégorie",
        "defaultValue": "@:configurator_category_dual_select.category.label…",
        "option": "{name} ({count})"
      },
      "subCategory": {
        "label": "Sous-catégorie",
        "defaultValue": "@:configurator_category_dual_select.subCategory.label…",
        "option": "@:configurator_category_dual_select.category.option",
        "defaultOption": "Aucune"
      }
    }
  }
}
</i18n>

<template>
  <div
    :class="[
      'configurator-category-dual-select',
      `configurator-category-dual-select--${orientation}`,
    ]"
  >
    <!-- Category -->
    <span
      :id="getLabelId('category')"
      class="configurator-category-dual-select__label"
    >
      {{ $t('configurator_category_dual_select.category.label') }}
    </span>

    <ConfiguratorCategorySelect
      :labelled-by="getLabelId('category')"
      :options="rootCategories"
      class="configurator-category-dual-select__select"
      :value="parentCategoryId"
      @change="onRootCategoryChange"
    >
      <!--suppress JSLastCommaInObjectLiteral -->
      <template #option="{ option }">
        {{
          $t('configurator_category_dual_select.category.option', {
            name: option.name,
            count: option.count,
          })
        }}
      </template>

      <template #placeholder>
        {{ $t('configurator_category_dual_select.category.defaultValue') }}
      </template>
    </ConfiguratorCategorySelect>

    <!-- Sub-category -->
    <span
      :id="getLabelId('sub-category')"
      class="configurator-category-dual-select__label"
      for=""
    >
      {{ $t('configurator_category_dual_select.subCategory.label') }}
    </span>

    <ConfiguratorCategorySelect
      :value="subCategoryId"
      :disabled="!parentCategoryId"
      :labelled-by="getLabelId('sub-category')"
      :options="subCategories"
      class="configurator-category-dual-select__select"
      @change="onSubCategoryChange"
    >
      <!--suppress JSLastCommaInObjectLiteral -->
      <template #option="{ option }">
        <template v-if="option.id === ''">
          {{
            $t('configurator_category_dual_select.subCategory.defaultOption')
          }}
        </template>
        <template v-else>
          {{
            $t('configurator_category_dual_select.subCategory.option', {
              name: option.name,
              count: option.count,
            })
          }}
        </template>
      </template>

      <template #placeholder>
        {{ $t('configurator_category_dual_select.subCategory.defaultValue') }}
      </template>
    </ConfiguratorCategorySelect>
  </div>
</template>

<script>
import {
  GlassesGetter,
  prefixType as prefixGlassType,
} from '../../store/modules/configurator/glasses';
import ConfiguratorCategorySelect from './ConfiguratorCategorySelect';
import { ROOT_CATEGORY_ID } from '../../api/static/categories';

export default {
  name: 'ConfiguratorCategoryDualSelect',
  components: { ConfiguratorCategorySelect },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Number,
      default: undefined,
    },
    orientation: {
      type: String,
      validator: value => ['horizontal', 'vertical'].includes(value),
      default: 'horizontal',
    },
  },
  data() {
    return {
      parentCategoryId: null,
      subCategoryId: null,
    };
  },
  computed: {
    /**
     *
     * @return {Category[]}
     */
    rootCategories() {
      return this.$store.getters[
        prefixGlassType(GlassesGetter.FIND_CATEGORY_BY_PARENT_ID)
      ](ROOT_CATEGORY_ID).filter(this.filterCategoriesHavingItems);
    },
    /**
     *
     * @return {Category[]}
     */
    subCategories() {
      const subCategories =
        (this.parentCategoryId &&
          this.$store.getters[
            prefixGlassType(GlassesGetter.FIND_CATEGORY_BY_PARENT_ID)
          ](this.parentCategoryId).filter(this.filterCategoriesHavingItems)) ||
        [];

      if (subCategories.length) {
        subCategories.unshift({ id: '' });
      }

      return subCategories;
    },
  },
  watch: {
    value: {
      handler(v) {
        /** @type {Category} */
        const category =
          this.$store.getters[
            prefixGlassType(GlassesGetter.FIND_CATEGORY_BY_ID)
          ](v);

        if (category) {
          if (category.parentId === ROOT_CATEGORY_ID) {
            this.parentCategoryId = category.id;
            this.subCategoryId = null;
          } else {
            this.parentCategoryId = category.parentId;
            this.subCategoryId = category.id;
          }
        } else {
          this.parentCategoryId = null;
          this.subCategoryId = null;
        }
      },
      immediate: true,
    },
  },
  methods: {
    getLabelId(v) {
      return `configurator-category-dual-select__label--${v}`;
    },
    emitValue(v) {
      this.$emit('change', v);
    },
    onRootCategoryChange(v) {
      this.parentCategoryId = v;
      this.subCategoryId = null;

      this.emitValue(v);
    },
    onSubCategoryChange(v) {
      if (v) {
        this.subCategoryId = v;

        this.emitValue(v);
      } else {
        this.subCategoryId = null;

        this.emitValue(this.parentCategoryId);
      }
    },
    /**
     *
     * @param {Category} category
     * @return {boolean}
     */
    filterCategoriesHavingItems(category) {
      return category.count > 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-category-dual-select {
  $block-selector: &;

  display: flex;
  gap: $configurator-mode-switch-gutter-horizontal;
  padding: 0 0.625rem;

  @include breakpoint($breakpoint-l) {
    padding: 0.625rem;
  }

  &--vertical {
    flex-direction: column;
    gap: 0.5rem;
  }

  &__label {
    @include sr-only();
  }

  &__select {
    flex: 1;
    min-width: 0;
    border-radius: 0.75rem;
    border: $configurator-category-select-vertical-border
      $configurator-category-select-border-color;
  }

  &::v-deep .custom-select {
    padding-right: 1rem;
    padding-left: 0.5rem;

    &--expanded {
      border-color: $color-arapawa;
      border-radius: 0.75rem;

      @include breakpoint($breakpoint-l) {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-bottom-color: transparent;
      }
    }
  }

  &::v-deep .custom-select__value,
  &::v-deep .custom-select__placeholder {
    padding: 1rem;
    color: $color-arapawa;
  }

  &::v-deep .custom-select__btn-toggle {
    &::before {
      @extend %icon--arrow-down;

      color: $color-arapawa;
      font-size: 1rem;
    }
  }
}
</style>
